import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import Layout from "../components/Layout/Layout";
import { useHotel } from "../contexts/HotelContext";
import { useToast } from "../contexts/ToastContext";
import FileUploadZone from "../components/files/FileUploadZone";
import FilesTable from "../components/files/FilesTable";
import FileTypeFilter from "../components/files/FileTypeFilter";
import { getFiles, uploadFile } from "../services/api";

interface FileFilters {
  includeArchived: boolean;
  category: string | null;
  fileType: string | null;
}

const FilesManager: React.FC = () => {
  const { user } = useHotel();
  const currentHotel = user?.defaultHotel;
  const { addToast } = useToast();
  const [filters, setFilters] = useState<FileFilters>({
    includeArchived: false,
    category: null,
    fileType: null,
  });

  const {
    data: files = [],
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ["files", currentHotel?.id, filters],
    queryFn: () =>
      getFiles({
        includeArchived: filters.includeArchived,
        category: filters.category || undefined,
        fileType: filters.fileType || undefined,
      }),
    enabled: !!currentHotel?.id,
  });

  const handleFileUpload = async (
    file: File,
    metadata: {
      displayName: string;
      description?: string;
      category?: string;
      tags?: string[];
    }
  ) => {
    if (!currentHotel?.id) return;

    try {
      await uploadFile(file, metadata);
      addToast({
        type: "success",
        message: `Plik "${metadata.displayName}" został przesłany pomyślnie`,
      });
      refetch();
    } catch (error) {
      console.error("Upload error:", error);
      addToast({
        type: "error",
        message: "Wystąpił błąd podczas przesyłania pliku",
      });
    }
  };

  const handleFilterChange = (newFilters: Partial<FileFilters>) => {
    setFilters((prev) => ({ ...prev, ...newFilters }));
  };

  if (error) {
    return (
      <Layout>
        <div className="p-6">
          <div className="bg-red-50 border border-red-200 rounded-md p-4">
            <div className="text-sm text-red-700">
              Wystąpił błąd podczas ładowania plików. Spróbuj ponownie.
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-semibold text-gray-900">
            Zarządzanie plikami
          </h1>
          <p className="mt-1 text-sm text-gray-600">
            Zarządzaj plikami ofertowymi swojego hotelu - cenniki, menu,
            prezentacje i inne materiały.
          </p>
        </div>

        {/* Upload Zone */}
        <div className="mb-6">
          <FileUploadZone onUpload={handleFileUpload} />
        </div>

        {/* Filters */}
        <div className="mb-6">
          <FileTypeFilter
            filters={filters}
            onFilterChange={handleFilterChange}
          />
        </div>

        {/* Files Table */}
        <div className="bg-white shadow rounded-lg">
          <FilesTable
            files={files}
            isLoading={isLoading}
            filters={filters}
            onRefresh={refetch}
          />
        </div>
      </div>
    </Layout>
  );
};

export default FilesManager;
